<script setup lang="ts">
import {computed, ComputedRef} from "vue";
import {ChevronLeft, ChevronRight} from "@lucide/vue";
import NotificationDetectionService from "@/notification/service/NotificationProgramService";
import {DetectionNotificationProps, DetectionPatient} from "@/notification/interfaces";
import {NotificationEdge} from "@/settings/interfaces";
import {useSystemStore} from "@/stores/system.store";
import {storeToRefs} from "pinia";
import {DETECTION_HEADER_HEIGHT, useNotificationStore} from "@/stores/notification.store";
import useNotificationDrag from "@/notification/composables/useNotificationDrag";
import {supportsWindowPlacement} from "@/core/services/shellCapabilities";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";

const props = defineProps<{
    isOpen: boolean;
    edge: NotificationEdge;
    item: DetectionNotificationProps | null;
    detecting: DetectionPatient | null;
}>();
const systemStore = useSystemStore();
const notificationStore = useNotificationStore();
const {groupingLogo} = storeToRefs(systemStore);
const emit = defineEmits(['toggle']);

/**
 * The strip is a drag handle for the notification window; a plain click keeps
 * toggling the drawer. The composable itself holds the auto-close countdown
 * for the duration of a drag.
 */
const {dragging, onPointerDown} = useNotificationDrag(
    () => props.isOpen,
    () => emit('toggle')
);

const programsCount: ComputedRef<number> = computed(() => {
    if (!props.item) {
        return 0;
    }

    const programs = NotificationDetectionService.consumerPrograms(props.item!.customer);

    return Object.values(programs).reduce((acc, programs) => acc + programs.length, 0);
});

/** Chevron pointing where a click will move the drawer: towards the pinned edge when open. */
const chevron = computed(() => {
    if (props.edge === 'left') {
        return props.isOpen ? ChevronLeft : ChevronRight;
    }

    return props.isOpen ? ChevronRight : ChevronLeft;
});

/**
 * Dragging needs the shell placement primitives AND the opt-in setting:
 * otherwise the strip is a plain toggle button.
 */
const canDrag = computed(() => supportsWindowPlacement() && notificationStore.dragEnabled);
</script>

<template>
  <div
    class="w-10 py-3 flex flex-col items-center justify-center gap-2 bg-primary-dark to-primary select-none notification-drag-handle"
    :class="{
      'rounded-l-2xl': edge === 'right',
      'rounded-r-2xl': edge === 'left',
      'bg-primary-dark': !groupingLogo,
      'bg-white': groupingLogo,
      'cursor-grabbing': dragging,
      'cursor-grab': canDrag && !dragging,
      'cursor-pointer': !canDrag,
    }"
    :style="isOpen ? {height: DETECTION_HEADER_HEIGHT + 'px'} : {}"
    @pointerdown="onPointerDown"
  >
    <div v-if="!isOpen">
      <img
        v-if="!groupingLogo"
        class="h-5 w-5"
        src="@/assets/images/logos/apodis-white.svg"
        alt="Logo Apodis"
        draggable="false"
      >
      <img
        v-else
        class="h-5 w-5 object-contain"
        :src="groupingLogo"
        alt="Logo Apodis"
        draggable="false"
      >
    </div>
    <component
      :is="chevron"
      v-if="!detecting"
      class="w-7 h-7"
      :class="{'text-white': !groupingLogo, 'text-primary': groupingLogo}"
    />
    <BaseFlexSpinner
      v-else
      color="white"
      size="button"
    />
    <div
      v-if="programsCount && !isOpen"
      class="w-4 h-4 flex items-center justify-center bg-red-500 rounded-full text-white text-xs"
    >
      <label class="leading-none">
        {{ programsCount }}
      </label>
    </div>
  </div>
</template>
